import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/Button';
import { motion } from 'framer-motion';
export function Authority() {
  const {
    t
  } = useLanguage();
  const stats = t('authority.stats');
  const pillars = t('authority.pillars');
  const financing = t('authority.financing');
  const easeOutCubic = [0.33, 1, 0.68, 1];
  const scrollToContact = () => {
    document.getElementById('contacto')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="py-20 lg:py-28 bg-brand-dark text-brand-cream relative overflow-hidden" data-id="element-27">
      {/* Subtle grain */}
      <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" data-id="element-28"></div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-29">
        <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-30">
          <span className="text-brand-gold/80 font-medium tracking-[0.25em] text-xs uppercase mb-6 block" data-id="element-31">
            {t('authority.eyebrow')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light mb-6 tracking-tight" data-id="element-32">
            {t('authority.heading')}
          </h2>
          <div className="w-12 h-px bg-brand-gold mx-auto mt-10" data-id="element-33"></div>
        </div>

        {/* Stats row */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.9,
        ease: easeOutCubic
      }} className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 max-w-5xl mx-auto mb-16 pb-14 border-b border-brand-cream/10" data-id="element-34">
          {stats.map((stat: any, idx: number) => <div key={idx} className="text-center" data-id="element-35">
              <p className="font-serif text-5xl lg:text-6xl text-brand-cream font-light mb-3" data-id="element-36">
                {stat.value}
              </p>
              <p className="text-[10px] font-medium tracking-widest uppercase text-brand-cream/50" data-id="element-37">
                {stat.label}
              </p>
            </div>)}
        </motion.div>

        {/* Pillars grid */}
        <div className="grid md:grid-cols-2 gap-10 lg:gap-14 max-w-5xl mx-auto mb-16" data-id="element-38">
          {pillars.map((pillar: any, idx: number) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.8,
          delay: idx * 0.15,
          ease: easeOutCubic
        }} className="flex gap-6" data-id="element-39">
              <span className="font-serif text-2xl text-brand-gold/80 font-light italic shrink-0" data-id="element-40">
                0{idx + 1}
              </span>
              <div data-id="element-41">
                <h3 className="font-serif text-2xl text-brand-cream font-light mb-3" data-id="element-42">
                  {pillar.title}
                </h3>
                <p className="text-brand-cream/60 font-light leading-relaxed" data-id="element-43">
                  {pillar.desc}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* Financing row */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.9,
        ease: easeOutCubic
      }} className="text-center max-w-3xl mx-auto pt-14 border-t border-brand-cream/10" data-id="element-44">
          <p className="text-[10px] font-medium tracking-widest uppercase text-brand-cream/40 mb-8" data-id="element-45">
            {t('authority.financingTitle')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 mb-12" data-id="element-46">
            {financing.map((name: string, idx: number) => <span key={idx} className="font-serif text-lg lg:text-xl text-brand-cream/70 font-light tracking-wider" data-id="element-47">
                {name}
              </span>)}
          </div>

          {/* Cherry pre-qualification block */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: easeOutCubic
        }} className="bg-brand-cream/5 border border-brand-gold/30 rounded-[2rem] px-8 py-10 lg:px-12 lg:py-12 max-w-2xl mx-auto" data-id="element-48">
            <div className="inline-flex items-center gap-3 mb-4" data-id="element-49">
              <span className="w-8 h-px bg-brand-gold" data-id="element-50"></span>
              <span className="text-brand-gold font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-51">
                Cherry · Financing Partner
              </span>
              <span className="w-8 h-px bg-brand-gold" data-id="element-52"></span>
            </div>

            <h3 className="font-serif text-3xl lg:text-4xl font-light text-brand-cream mb-3 tracking-tight" data-id="element-53">
              {t('authority.cherryTitle')}
            </h3>

            <p className="text-brand-cream/60 font-light text-sm lg:text-base mb-8" data-id="element-54">
              {t('authority.cherrySubtitle')}
            </p>

            <a href="https://pay.withcherry.com/dermalipo-phoenix-llc" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-brand-gold text-brand-dark px-8 py-4 rounded-full font-semibold text-xs lg:text-sm tracking-[0.15em] uppercase hover:bg-brand-cream transition-colors duration-300" data-id="element-55">
              {t('authority.cherryCta')}
              <span aria-hidden="true" data-id="element-56">→</span>
            </a>
          </motion.div>

          <div className="mt-16" data-id="element-57">
            <Button variant="luxury" size="lg" onClick={scrollToContact} className="border-brand-cream/30 text-brand-cream hover:bg-brand-cream hover:text-brand-dark" data-id="element-58">
              {t('nav.cta')}
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}